// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import React, { useEffect } from 'react';
import { GlobalTheme } from '@carbon/react';
import { useShellStore } from '../../stores/shellStore';

/**
 * Bridges the shell store's theme + accessibility preferences into the DOM,
 * mirroring the mockup's root attributes:
 *   - data-carbon-theme  (g100 | g10)   → also emitted by <GlobalTheme>
 *   - data-hc            (high contrast)
 *   - data-rm            (reduced motion)
 *   - data-large-text    (root font-size bump)
 *
 * Wrapping children in Carbon's <GlobalTheme> ensures portalled components
 * (Modal, Dropdown, OverflowMenu) inherit the active theme. We also set
 * data-carbon-theme on <html> so plain CSS keyed on the attribute (the Helios
 * theme layer in helios-theme.scss) resolves for the whole document.
 */
export default function ThemeEffects({ children }: { children: React.ReactNode }) {
  const theme = useShellStore(s => s.theme);
  const a11y = useShellStore(s => s.a11y);

  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute('data-carbon-theme', theme);
    root.setAttribute('data-hc', String(a11y.highContrast));
    root.setAttribute('data-rm', String(a11y.reduceMotion));
    root.setAttribute('data-large-text', String(a11y.largeText));

    // Carbon's <GlobalTheme> only provides React context — it does not emit the
    // theme's --cds-* custom properties into the DOM. @carbon/styles defines the
    // white token set on :root and the dark set under `.cds--g100`, so without
    // the class every real Carbon component (Tile/DataTable/Tabs/…) falls back to
    // the white tokens. Toggle the class on <body> (a descendant of the :root
    // <html>) so `.cds--g100 { --cds-*: … }` overrides the inherited white tokens
    // for the whole app, including portalled components (Modal/Dropdown/DatePicker).
    document.body.classList.toggle('cds--g100', theme === 'g100');
    document.body.classList.toggle('cds--g10', theme === 'g10');
  }, [theme, a11y.highContrast, a11y.reduceMotion, a11y.largeText]);

  return <GlobalTheme theme={theme}>{children}</GlobalTheme>;
}
